import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { api, ApiError } from '../api';
import { Icon } from '../components/Icon';
import { Link } from '../router';

export function ForgotPasswordPage() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api<{ passwordResetConfigured: boolean }>('/api/meta')
      .then((result) => { if (active) setAvailable(result.passwordResetConfigured); })
      .catch(() => { if (active) setAvailable(false); });
    return () => { active = false; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!available) return;
    setBusy(true);
    setError('');
    const data = new FormData(event.currentTarget);
    try {
      await api('/api/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email: data.get('email') }),
      });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecoveryCard eyebrow="Account recovery" title="Reset your password" description="Enter the email address on your Messavo account.">
      {available === false ? (
        <div className="recovery-result" role="status">
          <span><Icon name="support_agent" /></span>
          <h2>Email recovery is temporarily unavailable</h2>
          <p>Contact <a href="mailto:messavo@mehebub.com">messavo@mehebub.com</a> from your account email address. Never include your password or private message content.</p>
          <Link className="button button-tonal" to="/login">Back to sign in</Link>
        </div>
      ) : sent ? (
        <div className="recovery-result" role="status">
          <span><Icon name="mark_email_read" /></span>
          <h2>Check your email</h2>
          <p>If an account exists for that address, Messavo will send a single-use reset link. It expires after a short time.</p>
          <Link className="button button-tonal" to="/login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          <label>Email address<input name="email" type="email" autoComplete="email" maxLength={254} required autoFocus /></label>
          {error && <div className="form-error" role="alert"><Icon name="error" /> {error}</div>}
          <button className="button button-primary button-wide" disabled={busy || available === null}>{available === null ? 'Checking availability…' : busy ? 'Sending…' : 'Send reset link'}</button>
          <p className="recovery-help">For privacy, the result is the same whether or not an account exists.</p>
        </form>
      )}
    </RecoveryCard>
  );
}

export function ResetPasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token && window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [token]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const data = new FormData(event.currentTarget);
    const newPassword = String(data.get('newPassword'));
    if (newPassword !== String(data.get('confirmPassword'))) {
      setError('The passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
      setComplete(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecoveryCard eyebrow="Account recovery" title="Choose a new password" description="Use at least 12 characters that you do not use for another account.">
      {complete ? (
        <div className="recovery-result" role="status">
          <span><Icon name="verified_user" /></span>
          <h2>Password updated</h2>
          <p>All browser and mobile sign-in sessions were ended. You can now sign in with your new password.</p>
          <Link className="button button-primary" to="/login">Sign in</Link>
        </div>
      ) : !token ? (
        <div className="notice notice-error" role="alert">This reset link is incomplete. Request a new one to continue.</div>
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          <label>New password<input name="newPassword" type="password" autoComplete="new-password" minLength={12} maxLength={128} required autoFocus /><small>At least 12 characters.</small></label>
          <label>Confirm new password<input name="confirmPassword" type="password" autoComplete="new-password" minLength={12} maxLength={128} required /></label>
          {error && <div className="form-error" role="alert"><Icon name="error" /> {error}</div>}
          <button className="button button-primary button-wide" disabled={busy}>{busy ? 'Updating…' : 'Update password'}</button>
        </form>
      )}
      {!complete && <p className="recovery-help"><Link to="/forgot-password">Request a new reset link</Link></p>}
    </RecoveryCard>
  );
}

function RecoveryCard({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: string; children: ReactNode }) {
  return (
    <section className="recovery-page section-pad">
      <div className="recovery-card">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        {children}
      </div>
    </section>
  );
}
